import React, { useEffect, useRef, useState, useCallback } from "react";
import classNames from "classnames/bind";
import styles from "./Header.module.scss";

// Import components
import Navigation from "../../../components/Navigation/Navigation";
import Logo from "../../../components/Logo/Logo";
import ActionIcons from "../../../components/ActionIcons/ActionIcons";
import MobileHeader from "../../../components/MobileHeader/MobileHeader";
import MobileMenu from "../../../components/MobileMenu/MobileMenu";

// import stores 
import { useLanguageStore } from "../../../stores/Language/useLanguageStore";
// Import default menu data for fallback
import defaultMenuData from "../../../data/menuItems.json";

const cx = classNames.bind(styles);

const Header = () => {

  const { currentLanguage, translations, loadTranslations, setLanguage } = useLanguageStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);

  const mobileMenuRef = useRef(null);

  // Load translations when component mounts or language changes
  useEffect(() => {
    // Initialize language from localStorage if available
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && savedLanguage !== currentLanguage) {
      setLanguage(savedLanguage);
    }

    if (!translations[currentLanguage]) {
      loadTranslations(currentLanguage);
    }
  }, [currentLanguage, translations, setLanguage, loadTranslations]);


  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setActiveMobileDropdown(null);
  }, []);


  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".mobile-header__menu-button")
      ) {
        closeMobileMenu();
      }

      // Close search dropdown if clicking outside
      if (
        !event.target.closest(".mobile-header__search-dropdown") &&
        !event.target.closest(".mobile-header__search-button")
      ) {
        // Add logic to close search dropdown if needed
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Get current language translations with fallback
  const currentTranslations =
    translations[currentLanguage]
      ? translations[currentLanguage]
      : (translations['en'] || defaultMenuData.content);


  const menuData = currentTranslations.menu || defaultMenuData.content.menu || {};

  // Convert menu object to array format expected by Navigation component
  const menuItems = Object.entries(menuData).map(([key, value]) => ({
    name: value, // Display name in current language
    key: key,    // Original key for identification
    dropdown:
      (key === "Jewelry" && currentTranslations.JewelryDropDown) ||
      (key === "Gifts" && currentTranslations.GiftDropDown)
    // Add dropdown logic here if needed
  }));

  const handleMouseEnter = (index, item) => {
    if (item.dropdown) {
      setActiveDropdown(index);
    }
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchQuery("");
    }
  };

  const toggleMobileDropdown = (index) => {
    setActiveMobileDropdown(activeMobileDropdown === index ? null : index);
  };

  return (
    <header className={cx("header")}>
      <div className={cx("header__container")}>
        {/* Desktop Header */}
        <div className={cx("header__content")}>
          <Navigation
            menuItems={menuItems}
            activeDropdown={activeDropdown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />

          <Logo />

          <ActionIcons
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchPlaceholder={
              currentTranslations.PlaceHolder?.search_for_products ||
              defaultMenuData.content.PlaceHolder?.search_for_products ||
              "Search for products"
            }
          />


        </div>

        {/* Mobile Header */}
        <MobileHeader
          toggleMobileMenu={toggleMobileMenu}
          isSearchOpen={isSearchOpen}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          toggleSearch={toggleSearch}
        />

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
          menuItems={menuItems}
          activeMobileDropdown={activeMobileDropdown}
          toggleMobileDropdown={toggleMobileDropdown}
          mobileMenuRef={mobileMenuRef}
        />
      </div>
    </header>
  );
};

export default Header;